import React, { useEffect, useMemo, useRef, useState } from "react";
import { BadgeCheck, Check, ChevronRight, Copy, MessageCircle, X } from "lucide-react";
import { TURNSTILE_SITE_KEY } from "../../config/eventsApi";
import {
  BANK_DETAILS,
  extraPaymentNote,
  PAYMENT_HELPLINE,
} from "../../constants/formOptions";
import {
  compressImageFile,
  PAYMENT_SCREENSHOT_MAX_BYTES,
} from "../../utils/compressImage";
import {
  eventRegistrationComingSoon,
  eventRegistrationNotRequired,
  formatRupees,
  NO_REGISTRATION_NEEDED_LABEL,
  REGISTRATION_COMING_SOON_MESSAGE,
  REGISTRATIONS_OPENING_SOON_LABEL,
  submitPublicEventRegistration,
} from "../../utils/publicEventsApi";
import JoinWhatsappGroupLink from "../events/JoinWhatsappGroupLink";
import { toastActionError, toastActionSuccess } from "../../utils/registrationToasts";
import MarkdownText from "../common/MarkdownText";
import EventVenueName from "../events/EventVenueName";
import TurnstileWidget from "./TurnstileWidget";

const TEN_DIGIT_PHONE = /^\d{10}$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DOB_MIN = "1930-01-01";

const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
];

const FIELD_ORDER = [
  "name",
  "phone",
  "email",
  "dob",
  "gender",
  "memberType",
  "teammates",
  "notes",
  "paymentScreenshot",
  "turnstile",
];

async function copyText(text) {
  if (navigator.clipboard?.writeText && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      /* fall through to the execCommand fallback */
    }
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "");
  ta.style.position = "fixed";
  ta.style.top = "-9999px";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  let ok = false;
  try {
    ok = document.execCommand("copy");
  } catch {
    ok = false;
  }
  document.body.removeChild(ta);
  return ok;
}

const CopyButton = ({ value, label, toastMessage }) => {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleCopy = async () => {
    const ok = await copyText(String(value));
    if (!ok) {
      toastActionError("Could not copy. Please copy it manually.");
      return;
    }
    setCopied(true);
    if (toastMessage) toastActionSuccess(toastMessage);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), 1500);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleCopy}
        className={`smym-bank__copy${copied ? " smym-bank__copy--done" : ""}`}
        aria-label={label}
        title={copied ? "Copied" : label}
      >
        {copied ? <Check size={15} aria-hidden /> : <Copy size={15} aria-hidden />}
      </button>
      <span className="sr-only" role="status" aria-live="polite">
        {copied ? "Copied" : ""}
      </span>
    </>
  );
};

function isoDateOnly(value) {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" }).format(d);
}

function formatLongDate(iso) {
  const day = isoDateOnly(iso);
  if (!day) return "";
  return new Date(`${day}T00:00:00`).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/** Latest DOB that still meets `minAgeYears` on `asOn` (YYYY-MM-DD). */
function latestDobFor(minAgeYears, asOn) {
  if (!minAgeYears || !asOn) return "";
  const [y, m, d] = asOn.split("-").map(Number);
  const cutoff = new Date(y - minAgeYears, m - 1, d);
  const mm = String(cutoff.getMonth() + 1).padStart(2, "0");
  const dd = String(cutoff.getDate()).padStart(2, "0");
  return `${cutoff.getFullYear()}-${mm}-${dd}`;
}

function teamBounds(teamSize) {
  if (!teamSize) return null;
  const min = Math.max(1, Number(teamSize.min) || 1);
  const max = Math.max(min, Number(teamSize.max) || min);
  return { minOthers: min - 1, maxOthers: max - 1, min, max };
}

const emptyForm = {
  name: "",
  phone: "",
  email: "",
  dob: "",
  gender: "",
  memberType: "",
  notes: "",
};

/**
 * Generic public registration form for one event from the smym-app API. Fields are
 * driven by `event.formOptions`; posts multipart to `/api/public/events/:slug/registrations`.
 */
const EventRegistrationDrawer = ({ event, onClose }) => {
  const open = Boolean(event);
  const submitLockRef = useRef(false);
  const turnstileRef = useRef(null);
  const [form, setForm] = useState(emptyForm);
  const [teammates, setTeammates] = useState([]);
  const [paymentFile, setPaymentFile] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [result, setResult] = useState(null);

  const opts = useMemo(() => event?.formOptions || {}, [event]);
  const paymentNote = extraPaymentNote(opts.paymentInstructions);
  const team = teamBounds(opts.teamSize);
  const ageAsOn = isoDateOnly(opts.ageAsOn) || isoDateOnly(event?.date);
  const dobMax = opts.collectDob ? latestDobFor(opts.minAgeYears, ageAsOn) : "";

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  useEffect(() => {
    setForm(emptyForm);
    setTeammates(team ? Array(team.maxOthers).fill("") : []);
    setPaymentFile(null);
    setTurnstileToken("");
    setErrors({});
    setSubmitting(false);
    setSubmitError("");
    setResult(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.slug]);

  const fee = useMemo(() => {
    if (!event) return null;
    if (opts.memberChoice) {
      if (form.memberType === "member") return event.feeMember;
      if (form.memberType === "non_member") return event.feeNonMember;
      return null;
    }
    return event.feeNonMember;
  }, [event, opts.memberChoice, form.memberType]);

  const notRequired = eventRegistrationNotRequired(event);
  const comingSoon = eventRegistrationComingSoon(event);

  if (!open) return null;

  const fieldError = (field, value) => {
    switch (field) {
      case "name": {
        const v = String(value ?? "").trim();
        if (!v) return "Name is required.";
        if (v.length < 2 || v.length > 100)
          return "Enter your full name (2–100 characters).";
        return undefined;
      }
      case "phone":
        return TEN_DIGIT_PHONE.test(String(value ?? "").trim())
          ? undefined
          : "Enter a valid 10-digit WhatsApp number.";
      case "email":
        return EMAIL.test(String(value ?? "").trim())
          ? undefined
          : "Enter a valid email address.";
      case "dob":
        if (!opts.collectDob) return undefined;
        if (!value) return "Date of birth is required.";
        if (value < DOB_MIN) return "Enter a valid date of birth.";
        if (dobMax && value > dobMax) {
          return `You must be at least ${opts.minAgeYears} years old as on ${formatLongDate(ageAsOn)}.`;
        }
        return undefined;
      case "gender":
        return opts.collectGender && !value ? "Select your gender." : undefined;
      case "memberType":
        return opts.memberChoice && !value ? "Select Member or Non-member." : undefined;
      case "teammates": {
        if (!team) return undefined;
        const filled = (value || []).map((t) => t.trim()).filter(Boolean);
        if (filled.length < team.minOthers) {
          return team.minOthers === 1
            ? "Add your teammate's name."
            : `Add at least ${team.minOthers} teammate names.`;
        }
        return undefined;
      }
      case "notes":
        if (!opts.notesQuestion) return undefined;
        if (opts.notesRequired && !String(value ?? "").trim())
          return "This answer is required.";
        if (String(value ?? "").length > 1000)
          return "Keep this under 1000 characters.";
        return undefined;
      case "paymentScreenshot":
        return opts.paymentRequired && !value
          ? "Payment screenshot is required."
          : undefined;
      default:
        return undefined;
    }
  };

  const applyFieldError = (field, value) => {
    const msg = fieldError(field, value);
    setErrors((prev) => {
      const next = { ...prev };
      if (msg) next[field] = msg;
      else delete next[field];
      return next;
    });
  };

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) applyFieldError(field, value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (eventRegistrationNotRequired(event) || eventRegistrationComingSoon(event)) {
      return;
    }
    setSubmitError("");

    const next = {};
    for (const field of Object.keys(emptyForm)) {
      const msg = fieldError(field, form[field]);
      if (msg) next[field] = msg;
    }
    const teamErr = fieldError("teammates", teammates);
    if (teamErr) next.teammates = teamErr;
    const payErr = fieldError("paymentScreenshot", paymentFile);
    if (payErr) next.paymentScreenshot = payErr;
    if (TURNSTILE_SITE_KEY && !turnstileToken)
      next.turnstile = "Please complete the security check.";
    setErrors(next);
    const firstErr = FIELD_ORDER.map((f) => next[f]).find(Boolean);
    if (firstErr) {
      toastActionError(firstErr);
      return;
    }

    if (submitLockRef.current) return;
    submitLockRef.current = true;
    setSubmitting(true);
    try {
      const body = new FormData();
      body.set("name", form.name.trim());
      body.set("phone", form.phone.trim());
      body.set("email", form.email.trim());
      if (opts.collectDob) body.set("dob", form.dob);
      if (opts.collectGender) body.set("gender", form.gender);
      if (opts.memberChoice) body.set("memberType", form.memberType);
      if (team) {
        body.set(
          "teammates",
          JSON.stringify(teammates.map((t) => t.trim()).filter(Boolean)),
        );
      }
      if (opts.notesQuestion) body.set("notes", form.notes.trim());
      if (opts.paymentRequired && paymentFile) {
        const compressed = await compressImageFile(paymentFile);
        if (compressed.size > PAYMENT_SCREENSHOT_MAX_BYTES) {
          throw Object.assign(
            new Error("This screenshot is too large. Please crop it and retry."),
            {
              field: "paymentScreenshot",
            },
          );
        }
        body.set("paymentScreenshot", compressed, compressed.name);
      }
      if (TURNSTILE_SITE_KEY) body.set("cf-turnstile-response", turnstileToken);

      const data = await submitPublicEventRegistration(event.slug, body);
      setResult({
        ...data.registration,
        whatsappGroupUrl: data.whatsappGroupUrl || opts.whatsappGroupUrl || "",
      });
    } catch (err) {
      const msg = err?.message || "Something went wrong. Please try again.";
      const field =
        err?.field === "paymentScreenshot" ? "paymentScreenshot" : err?.field;
      if (field && FIELD_ORDER.includes(field)) {
        setErrors((prev) => ({ ...prev, [field]: msg }));
      } else if (
        err?.code === "unsupported_file_type" ||
        err?.code === "file_too_large"
      ) {
        setErrors((prev) => ({ ...prev, paymentScreenshot: msg }));
      } else {
        setSubmitError(msg);
      }
      toastActionError(msg);
      // Tokens are single-use: always fetch a fresh one before the next attempt.
      turnstileRef.current?.reset();
    } finally {
      submitLockRef.current = false;
      setSubmitting(false);
    }
  };

  const dateLabel = event.endsAtEndOfDay
    ? `Until ${formatLongDate(event.date)}`
    : formatLongDate(event.date);
  const titleId = "event-reg-drawer-title";

  return (
    <div className="smym-drawer-backdrop" onClick={onClose} role="presentation">
      <aside
        className="smym-drawer"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="smym-drawer__head">
          <div className="min-w-0">
            <p className="smym-drawer__eyebrow">
              SMYM Chennai{dateLabel ? ` · ${dateLabel}` : ""}
            </p>
            <h2 id={titleId} className="smym-drawer__title">
              {notRequired || comingSoon
                ? event.name
                : result
                  ? "Registration received"
                  : `Register for ${event.name}`}
            </h2>
            {!result && event.venue ? (
              <p className="smym-drawer__sub">
                {event.time ? `${event.time} · ` : ""}
                <EventVenueName event={event} />
              </p>
            ) : null}
            {!result && event.sponsors?.length > 0 ? (
              <div className="smym-drawer__sponsors">
                <span className="smym-drawer__sponsors-label">Sponsored by</span>{" "}
                {event.sponsors.map((sponsor, index) => (
                  <React.Fragment key={`${sponsor}-${index}`}>
                    {index > 0 ? " & " : null}
                    <MarkdownText inline>{sponsor}</MarkdownText>
                  </React.Fragment>
                ))}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="smym-drawer__close"
            aria-label={notRequired || comingSoon ? "Close" : "Close registration form"}
          >
            <X size={22} />
          </button>
        </div>

        {notRequired ? (
          <div className="smym-drawer__form">
            <div className="smym-drawer__body">
              <p className="font-semibold text-[var(--smym-ink)]">
                {NO_REGISTRATION_NEEDED_LABEL}
              </p>
              <JoinWhatsappGroupLink
                event={event}
                className="smym-btn smym-btn-primary w-full"
              />
            </div>
            <div className="smym-drawer__foot">
              <button type="button" onClick={onClose} className="smym-modal__dismiss">
                Close
              </button>
            </div>
          </div>
        ) : comingSoon ? (
          <div className="smym-drawer__form">
            <div className="smym-drawer__body">
              <p className="font-semibold text-[var(--smym-ink)]" role="status">
                {REGISTRATIONS_OPENING_SOON_LABEL}
              </p>
              <p>{REGISTRATION_COMING_SOON_MESSAGE}</p>
            </div>
            <div className="smym-drawer__foot">
              <button type="button" onClick={onClose} className="smym-modal__dismiss">
                Close
              </button>
            </div>
          </div>
        ) : result ? (
          <SuccessPanel result={result} onClose={onClose} />
        ) : (
          <form onSubmit={handleSubmit} className="smym-drawer__form" noValidate>
            <div className="smym-drawer__body">
              <JoinWhatsappGroupLink
                event={event}
                className="smym-btn smym-btn-outline w-full"
              />
              <div>
                <label htmlFor="event-reg-name" className="smym-field__label">
                  Name <span className="smym-field__req">*</span>
                </label>
                <input
                  id="event-reg-name"
                  type="text"
                  autoComplete="name"
                  maxLength={100}
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  onBlur={(e) => applyFieldError("name", e.target.value)}
                  className="smym-field__input"
                  aria-invalid={errors.name ? "true" : "false"}
                />
                {errors.name ? (
                  <p className="smym-field__error">{errors.name}</p>
                ) : null}
              </div>

              <div>
                <label htmlFor="event-reg-phone" className="smym-field__label">
                  WhatsApp phone number <span className="smym-field__req">*</span>
                </label>
                <input
                  id="event-reg-phone"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  maxLength={10}
                  placeholder="10-digit number"
                  value={form.phone}
                  onChange={(e) =>
                    setField("phone", e.target.value.replace(/\D/g, "").slice(0, 10))
                  }
                  onBlur={(e) => applyFieldError("phone", e.target.value)}
                  className="smym-field__input tabular-nums"
                  aria-invalid={errors.phone ? "true" : "false"}
                />
                {errors.phone ? (
                  <p className="smym-field__error">{errors.phone}</p>
                ) : null}
              </div>

              <div>
                <label htmlFor="event-reg-email" className="smym-field__label">
                  Email <span className="smym-field__req">*</span>
                </label>
                <input
                  id="event-reg-email"
                  type="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  onBlur={(e) => applyFieldError("email", e.target.value)}
                  className="smym-field__input"
                  aria-invalid={errors.email ? "true" : "false"}
                />
                {errors.email ? (
                  <p className="smym-field__error">{errors.email}</p>
                ) : null}
              </div>

              {opts.collectDob ? (
                <div>
                  <label htmlFor="event-reg-dob" className="smym-field__label">
                    Date of birth <span className="smym-field__req">*</span>
                  </label>
                  <input
                    id="event-reg-dob"
                    type="date"
                    min={DOB_MIN}
                    max={dobMax || undefined}
                    value={form.dob}
                    onChange={(e) => setField("dob", e.target.value)}
                    onBlur={(e) => applyFieldError("dob", e.target.value)}
                    className="smym-field__input"
                    aria-invalid={errors.dob ? "true" : "false"}
                  />
                  {opts.minAgeYears && ageAsOn ? (
                    <p className="smym-field__hint">
                      You must be at least {opts.minAgeYears} years old as on{" "}
                      {formatLongDate(ageAsOn)}.
                    </p>
                  ) : null}
                  {errors.dob ? (
                    <p className="smym-field__error">{errors.dob}</p>
                  ) : null}
                </div>
              ) : null}

              {opts.collectGender ? (
                <ChoiceGroup
                  name="event-reg-gender"
                  label="Gender"
                  options={GENDER_OPTIONS}
                  value={form.gender}
                  error={errors.gender}
                  columns={3}
                  onChange={(v) => {
                    setField("gender", v);
                    applyFieldError("gender", v);
                  }}
                />
              ) : null}

              {opts.memberChoice ? (
                <ChoiceGroup
                  name="event-reg-member-type"
                  label="Membership"
                  options={[
                    {
                      value: "member",
                      label: "SMYM Member",
                      amount: formatRupees(event.feeMember),
                    },
                    {
                      value: "non_member",
                      label: "Non-member",
                      amount: formatRupees(event.feeNonMember),
                    },
                  ]}
                  value={form.memberType}
                  error={errors.memberType}
                  onChange={(v) => {
                    setField("memberType", v);
                    applyFieldError("memberType", v);
                  }}
                />
              ) : null}

              {team && team.maxOthers > 0 ? (
                <fieldset className="smym-choice">
                  <legend className="smym-field__label">
                    Teammates
                    {team.minOthers > 0 ? (
                      <span className="smym-field__req"> *</span>
                    ) : null}
                  </legend>
                  <p className="smym-field__hint">
                    {team.min === team.max
                      ? `Teams of ${team.max}, including you.`
                      : `Teams of ${team.min}–${team.max}, including you.`}
                  </p>
                  <div className="smym-teammates">
                    {teammates.map((mate, i) => (
                      <input
                        key={i}
                        type="text"
                        aria-label={`Teammate ${i + 2} name`}
                        placeholder={`Teammate ${i + 2}${i < team.minOthers ? "" : " (optional)"}`}
                        value={mate}
                        maxLength={100}
                        onChange={(e) => {
                          const nextMates = teammates.slice();
                          nextMates[i] = e.target.value;
                          setTeammates(nextMates);
                          if (errors.teammates) applyFieldError("teammates", nextMates);
                        }}
                        className="smym-field__input"
                        aria-invalid={errors.teammates ? "true" : "false"}
                      />
                    ))}
                  </div>
                  {errors.teammates ? (
                    <p className="smym-field__error">{errors.teammates}</p>
                  ) : null}
                </fieldset>
              ) : null}

              {opts.notesQuestion ? (
                <div>
                  <label htmlFor="event-reg-notes" className="smym-field__label">
                    {opts.notesQuestion}
                    {opts.notesRequired ? (
                      <span className="smym-field__req"> *</span>
                    ) : null}
                  </label>
                  <textarea
                    id="event-reg-notes"
                    rows={3}
                    maxLength={1000}
                    value={form.notes}
                    onChange={(e) => setField("notes", e.target.value)}
                    onBlur={(e) => applyFieldError("notes", e.target.value)}
                    className="smym-field__input"
                    aria-invalid={errors.notes ? "true" : "false"}
                  />
                  {errors.notes ? (
                    <p className="smym-field__error">{errors.notes}</p>
                  ) : null}
                </div>
              ) : null}

              {opts.paymentRequired ? (
                <div className="smym-drawer__pay">
                  <h3 className="smym-drawer__pay-title">Payment</h3>
                  {fee != null ? (
                    <p className="smym-drawer__pay-fee">
                      Amount to pay:{" "}
                      <strong className="tabular-nums">{formatRupees(fee)}</strong>
                    </p>
                  ) : opts.memberChoice ? (
                    <p className="smym-drawer__pay-note">
                      Choose your membership to see the amount.
                    </p>
                  ) : null}
                  <p className="smym-drawer__pay-note">
                    Transfer to the SMYM account below, then upload the payment
                    screenshot. Your registration is confirmed once the payment is
                    verified.
                  </p>
                  {event.upiId || event.upiQrUrl ? (
                    <dl className="smym-bank">
                      {event.upiId ? (
                        <div className="smym-bank__row">
                          <dt className="smym-bank__label">UPI ID</dt>
                          <dd className="smym-bank__value smym-bank__value--mono smym-bank__value--copy">
                            <span>{event.upiId}</span>
                            <CopyButton
                              value={event.upiId}
                              label="Copy UPI ID"
                              toastMessage="UPI ID copied"
                            />
                          </dd>
                        </div>
                      ) : null}
                      {event.upiQrUrl ? (
                        <img
                          src={event.upiQrUrl}
                          alt={`UPI QR code for ${event.name}`}
                          className="smym-pay-qr"
                          loading="lazy"
                        />
                      ) : null}
                    </dl>
                  ) : null}
                  <dl className="smym-bank">
                    <div className="smym-bank__row">
                      <dt className="smym-bank__label">Account name</dt>
                      <dd className="smym-bank__value">{BANK_DETAILS.name}</dd>
                    </div>
                    <div className="smym-bank__row">
                      <dt className="smym-bank__label">Bank</dt>
                      <dd className="smym-bank__value">{BANK_DETAILS.bankName}</dd>
                    </div>
                    <div className="smym-bank__row">
                      <dt className="smym-bank__label">Branch</dt>
                      <dd className="smym-bank__value">{BANK_DETAILS.branch}</dd>
                    </div>
                    <div className="smym-bank__row">
                      <dt className="smym-bank__label">Account no.</dt>
                      <dd className="smym-bank__value smym-bank__value--mono smym-bank__value--copy">
                        <span>{BANK_DETAILS.accountNumber}</span>
                        <CopyButton
                          value={BANK_DETAILS.accountNumber}
                          label="Copy account number"
                          toastMessage="Account number copied"
                        />
                      </dd>
                    </div>
                    <div className="smym-bank__row">
                      <dt className="smym-bank__label">IFSC</dt>
                      <dd className="smym-bank__value smym-bank__value--mono smym-bank__value--copy">
                        <span>{BANK_DETAILS.ifscCode}</span>
                        <CopyButton
                          value={BANK_DETAILS.ifscCode}
                          label="Copy IFSC code"
                          toastMessage="IFSC code copied"
                        />
                      </dd>
                    </div>
                  </dl>
                  <p className="smym-drawer__pay-note">
                    For payment issues, WhatsApp{" "}
                    <a
                      href={`https://wa.me/91${PAYMENT_HELPLINE.digits10}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="smym-drawer__link"
                    >
                      {PAYMENT_HELPLINE.display}
                    </a>
                    .
                  </p>
                  {paymentNote ? (
                    <MarkdownText className="smym-drawer__pay-note smym-pay-instructions">
                      {paymentNote}
                    </MarkdownText>
                  ) : null}
                  <div>
                    <label htmlFor="event-reg-payment" className="smym-field__label">
                      Payment screenshot <span className="smym-field__req">*</span>
                    </label>
                    <input
                      id="event-reg-payment"
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(e) => {
                        const f = e.target.files?.[0] ?? null;
                        setPaymentFile(f);
                        applyFieldError("paymentScreenshot", f);
                      }}
                      className="smym-field__file"
                      aria-invalid={errors.paymentScreenshot ? "true" : "false"}
                    />
                    {errors.paymentScreenshot ? (
                      <p className="smym-field__error">{errors.paymentScreenshot}</p>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {TURNSTILE_SITE_KEY ? (
                <div>
                  <TurnstileWidget
                    ref={turnstileRef}
                    siteKey={TURNSTILE_SITE_KEY}
                    onToken={(token) => {
                      setTurnstileToken(token);
                      if (token) {
                        setErrors((prev) => {
                          if (!prev.turnstile) return prev;
                          const rest = { ...prev };
                          delete rest.turnstile;
                          return rest;
                        });
                      }
                    }}
                    onError={(msg) =>
                      setErrors((prev) => ({ ...prev, turnstile: msg }))
                    }
                  />
                  {errors.turnstile ? (
                    <p className="smym-field__error">{errors.turnstile}</p>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="smym-drawer__foot">
              {submitError ? (
                <p className="smym-drawer__alert" role="alert">
                  {submitError}
                </p>
              ) : null}
              <button
                type="submit"
                disabled={submitting}
                className="smym-btn smym-btn-primary smym-drawer__submit"
              >
                {submitting ? (
                  "Submitting…"
                ) : (
                  <>
                    Submit
                    <ChevronRight className="h-5 w-5 shrink-0" aria-hidden />
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </aside>
    </div>
  );
};

const ChoiceGroup = ({ name, label, options, value, error, onChange, columns = 2 }) => {
  const labelId = `${name}-label`;
  const errorId = `${name}-error`;
  return (
    <fieldset className="smym-choice">
      <legend id={labelId} className="smym-field__label">
        {label} <span className="smym-field__req">*</span>
      </legend>
      <div
        className="smym-choice__grid"
        style={
          columns !== 2
            ? { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }
            : undefined
        }
        role="radiogroup"
        aria-labelledby={labelId}
        aria-required="true"
        aria-invalid={error ? "true" : "false"}
        aria-describedby={error ? errorId : undefined}
      >
        {options.map((opt) => (
          <label
            key={opt.value}
            className={`smym-choice__option${value === opt.value ? " smym-choice__option--on" : ""}`}
          >
            <input
              type="radio"
              name={name}
              value={opt.value}
              checked={value === opt.value}
              onChange={() => onChange(opt.value)}
              className="smym-choice__input"
            />
            <span className="smym-choice__label">{opt.label}</span>
            {opt.amount ? (
              <span className="smym-choice__amount tabular-nums">{opt.amount}</span>
            ) : null}
          </label>
        ))}
      </div>
      {error ? (
        <p id={errorId} className="smym-field__error">
          {error}
        </p>
      ) : null}
    </fieldset>
  );
};

const SuccessPanel = ({ result, onClose }) => {
  const groupUrl = String(result.whatsappGroupUrl || "").trim();
  const paymentPending = result.paymentStatus === "pending";
  return (
    <div className="smym-drawer__form">
      <div
        className="smym-drawer__body smym-reg-success"
        role="status"
        aria-live="polite"
      >
        <div className="smym-modal__icon" aria-hidden>
          <BadgeCheck size={30} strokeWidth={2.25} />
        </div>
        <p className="smym-reg-success__lead">
          Thank you{result.name ? `, ${result.name.split(" ")[0]}` : ""}. You&apos;re
          registered for <strong>{result.eventTitle}</strong>.
        </p>
        <div className="smym-reg-success__number">
          <span className="smym-bank__label">Registration number</span>
          <span className="smym-bank__value--copy smym-reg-success__value">
            <span>{result.regNumber}</span>
            <CopyButton
              value={result.regNumber}
              label="Copy registration number"
              toastMessage="Registration number copied"
            />
          </span>
        </div>
        <p className="smym-drawer__pay-note">
          Save this number — quote it if you contact the organisers.
          {paymentPending
            ? ` Your payment${result.amountDue != null ? ` of ${formatRupees(result.amountDue)}` : ""} will be verified by the team.`
            : ""}
        </p>
        {groupUrl ? (
          <a
            href={groupUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="smym-btn smym-btn-primary smym-modal__cta"
          >
            <MessageCircle className="h-5 w-5 shrink-0" aria-hidden />
            Join the WhatsApp group
          </a>
        ) : null}
      </div>
      <div className="smym-drawer__foot">
        <button type="button" onClick={onClose} className="smym-modal__dismiss">
          Done
        </button>
      </div>
    </div>
  );
};

export default EventRegistrationDrawer;
